import {API_BASE_URL} from '../constant/constant';

const API_BASE = `${API_BASE_URL}/support`;

// Helper function to get auth headers
const getAuthHeaders = (token) => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${token}`
});

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }
  return response.json();
};

// 1. Create Support Ticket
export const createSupportTicket = async (ticketData, token) => {
  try {
    const response = await fetch(`${API_BASE}/create`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(ticketData)
    });
    return await handleResponse(response);
  } catch (error) {
    console.error('Error creating support ticket:', error);
    throw error;
  }
};

// 2. Get User's Tickets
export const getUserTickets = async (params = {}, token) => {
  try {
    const queryParams = new URLSearchParams();
    
    // Add query parameters
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });

    const url = `${API_BASE}/my-tickets${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(token)
    });
    return await handleResponse(response);
  } catch (error) {
    console.error('Error fetching user tickets:', error);
    throw error;
  }
};

// 3. Get Specific Ticket
export const getTicketById = async (ticketId, token) => {
  try {
    const response = await fetch(`${API_BASE}/${ticketId}`, {
      method: 'GET',
      headers: getAuthHeaders(token)
    });
    return await handleResponse(response);
  } catch (error) {
    console.error('Error fetching ticket:', error);
    throw error;
  }
};

// 4. Add Response to Ticket
export const addTicketResponse = async (ticketId, responseData, token) => {
  try {
    const response = await fetch(`${API_BASE}/${ticketId}/respond`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(responseData)
    });
    return await handleResponse(response);
  } catch (error) {
    console.error('Error adding ticket response:', error);
    throw error;
  }
};

// 5. Update Ticket Status
export const updateTicketStatus = async (ticketId, status, token) => {
  try {
    const response = await fetch(`${API_BASE}/${ticketId}/status`, {
      method: 'PATCH',
      headers: getAuthHeaders(token),
      body: JSON.stringify({ status })
    });
    return await handleResponse(response);
  } catch (error) {
    console.error('Error updating ticket status:', error);
    throw error;
  }
};

// 6. Update Ticket Details
export const updateTicketDetails = async (ticketId, ticketData, token) => {
  try {
    const response = await fetch(`${API_BASE}/${ticketId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(token),
      body: JSON.stringify(ticketData)
    });
    return await handleResponse(response);
  } catch (error) {
    console.error('Error updating ticket details:', error);
    throw error;
  }
};

// 7. Delete Ticket
export const deleteTicket = async (ticketId, token) => {
  try {
    const response = await fetch(`${API_BASE}/${ticketId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(token)
    });
    return await handleResponse(response);
  } catch (error) {
    console.error('Error deleting ticket:', error);
    throw error;
  }
};

// 8. Get Ticket Statistics
export const getTicketStats = async (token) => {
  try {
    const response = await fetch(`${API_BASE}/stats/overview`, {
      method: 'GET',
      headers: getAuthHeaders(token)
    });
    return await handleResponse(response);
  } catch (error) {
    console.error('Error fetching ticket stats:', error);
    throw error;
  }
};

// Utility functions for form validation
export const validateTicketData = (ticketData) => {
  const errors = {};
  
  if (!ticketData.subject || ticketData.subject.trim().length < 5) {
    errors.subject = 'Subject must be at least 5 characters long';
  }
  
  if (!ticketData.description || ticketData.description.trim().length < 10) {
    errors.description = 'Description must be at least 10 characters long';
  }
  
  if (!ticketData.category) {
    errors.category = 'Category is required';
  }
  
  if (!ticketData.priority) {
    errors.priority = 'Priority is required';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Constants for dropdowns
export const TICKET_CATEGORIES = [
  'Technical',
  'Billing',
  'Account',
  'Feature Request',
  'Bug Report',
  'General Inquiry'
];

export const TICKET_PRIORITIES = [
  'Low',
  'Medium',
  'High',
  'Urgent'
];

export const TICKET_STATUSES = [
  'Open',
  'In Progress',
  'Resolved',
  'Closed'
];
